import React, { useEffect, useState } from 'react';
import Pagination from '../shared/Pagination';
import ThinScroll from '../shared/ThinScroll';
import { Skeleton, RefetchBar } from '../shared/Skeleton';
import { expandableRowProps } from '../shared/expandableRow';
import NoRecords from '../shared/NoRecords';

/**
 * Client-side page size for tables the backend does NOT paginate — every panel except the
 * query/page tables and the two health tables, which are server-paged at the envelope's
 * own limit (see DetailTable/HealthSummary) and are left untouched here.
 */
const CLIENT_PAGE_SIZE = 15;

/**
 * The table primitive behind `top_queries`, `top_pages`, `device_split`,
 * `search_appearance` and both health panels.
 *
 * ── Built on the house table, not beside it (spec v2 §4) ────────────────────────
 * §4 hands type, colour and layout to us and settles ties explicitly: "consistency
 * across the dashboard wins over this page having its own look." So this uses the same
 * three primitives every other dashboard table does, rather than its own versions:
 *
 *  - `responsive-card-table` — the shared CSS that turns a table into a labelled card
 *    list below 640px, reading each cell's `data-label`. This replaces a hand-rolled
 *    second `<ul>` render of every row: two markup paths for the same data meant a
 *    column added to one could silently miss the other, and the a11y sweep already had
 *    to special-case the duplicate.
 *  - `expandableRowProps` — makes the WHOLE row the control (role, tabIndex, Enter and
 *    Space, `aria-expanded`, focus ring), which is how the GA4 and Ads drill-downs work.
 *    The previous `+`/`−` button in its own column was reachable, but it was not the
 *    row — so the large obvious target did nothing.
 *  - `RefetchBar` — see the loading note below.
 *
 * ── Loading: keep the reader's place ────────────────────────────────────────────
 * A first load with no rows yet draws a skeleton. A REFETCH — paging, a verdict filter,
 * a range change — keeps the existing rows on screen, dims them, and runs the shared
 * progress bar along the top. Replacing rows with a skeleton on every page click makes
 * the table flash and loses the reader's position, which is the reason the house pattern
 * exists at all.
 *
 * Every cell value arrives pre-formatted from the caller's `render` — this component
 * computes nothing (§8).
 *
 * @param {Array<{key:string,label:string,align?:'left'|'right',sortable?:boolean,render:(row:object)=>React.ReactNode,title?:(row:object)=>string}>} columns
 * @param {Array<object>} rows in the order the payload returned them — never re-sorted (§8)
 * @param {(row:object,index:number)=>string} rowKey
 * @param {{page:number,limit:number,total_items:number,total_pages:number}} [pagination]
 * @param {(page:number)=>void} [onPageChange] omit to render rows without paging controls
 * @param {(row:object)=>boolean} [isRowHighlighted]
 * @param {Array<{key:string,label:string,render:(row:object)=>React.ReactNode}>} [expandColumns]
 * @param {string} [ariaLabel] the panel sentence — the table's text alternative
 * @param {{key:string,dir:'asc'|'desc'}|null} [sort] the active sort, for the header arrow
 * @param {(key:string)=>void} [onSort] omit to leave headers inert
 */
const PaginatedTable = ({
  columns,
  rows,
  rowKey,
  pagination,
  onPageChange,
  isRowHighlighted,
  expandColumns,
  label = 'rows',
  loading = false,
  // Left undefined so the shared empty state uses its uniform default ("No data for this
  // period" / "…try a wider range."); callers no longer pass per-table wording.
  emptyMessage = undefined,
  ariaLabel,
  sort = null,
  onSort,
}) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const hasExpansion = Array.isArray(expandColumns) && expandColumns.length > 0;
  const keyFor = (row, i) => (rowKey ? rowKey(row, i) : String(i));
  const list = Array.isArray(rows) ? rows : [];

  // Tables the backend does not paginate (no `pagination`/`onPageChange`) are paged here,
  // 15 rows at a time. The server-paged tables keep their own paging untouched.
  const serverPaged = Boolean(onPageChange && pagination);
  const clientPaged = !serverPaged && list.length > CLIENT_PAGE_SIZE;
  const [clientPage, setClientPage] = useState(1);
  // Snap back to page 1 whenever the dataset size changes — a filter or a property switch
  // must not strand the reader on a page the new, shorter result set no longer has.
  useEffect(() => { setClientPage(1); }, [list.length]);
  const clientTotalPages = clientPaged ? Math.ceil(list.length / CLIENT_PAGE_SIZE) : 1;
  const safeClientPage = Math.min(Math.max(clientPage, 1), clientTotalPages);
  const visibleList = clientPaged
    ? list.slice((safeClientPage - 1) * CLIENT_PAGE_SIZE, safeClientPage * CLIENT_PAGE_SIZE)
    : list;

  // First load — there is nothing to keep, so shape the wait like the rows that are
  // coming rather than dimming an empty box.
  if (loading && list.length === 0) {
    return (
      <div className="space-y-2" role="status" aria-label="Loading">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-9" />
        ))}
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  if (list.length === 0) {
    return <NoRecords message={emptyMessage} />;
  }

  // A field with no value FOR THIS ROW is omitted rather than rendered as a blank
  // definition — which is also how the spec's "drop the column when every row is null"
  // rule is satisfied (Google returns the deprecated `indexed_*` counts as null on every
  // row), while still handling the mixed case where a field is present on some pages
  // and not others.
  const presentExtras = (row) =>
    (expandColumns || []).filter((c) => {
      const content = c.render(row);
      return content !== null && content !== undefined && content !== '';
    });

  /**
   * The drill-down body for one row.
   *
   * Each pair is its own two-column grid rather than a `flex gap-2`, so the values line
   * up under one another instead of starting wherever their label happened to end —
   * "Submitted  Jun 6, 2021" and "Pages indexed  0" were previously ragged down the
   * column and had to be read one at a time.
   *
   * Three columns from `lg` up: an inspection row carries eleven extra fields and a
   * sitemap nine, and two columns on a 1400px card left most of the width empty while
   * still running long.
   */
  const expansionPanel = (row) => (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm text-left">
      {presentExtras(row).map((c) => (
        <div key={c.key} className="grid grid-cols-[minmax(0,11rem)_1fr] gap-x-3 items-baseline">
          <dt className="text-gray-500 dark:text-gray-400 truncate" title={c.label}>{c.label}</dt>
          <dd className="text-gray-800 dark:text-gray-200 break-words min-w-0 font-medium">{c.render(row)}</dd>
        </div>
      ))}
    </dl>
  );

  const columnCount = columns.length;

  return (
    // `relative` anchors `RefetchBar`, which positions absolutely against its host.
    <div className="relative">
      {loading && <RefetchBar />}

      <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        <ThinScroll className="responsive-card-table-wrapper overflow-x-auto">
          <table className="responsive-card-table w-full text-sm" aria-label={ariaLabel}>
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 text-[11px] text-gray-500 dark:text-gray-400">
                {columns.map((c) => {
                  // Sorting is OPT-IN per column and only offered when the caller passed
                  // `onSort`. The default table is the payload's own order, which §8
                  // requires be left alone; a header that reorders rows on a table whose
                  // owner never asked for sorting would be exactly that violation.
                  const sortable = Boolean(onSort) && c.sortable !== false;
                  const active = sort?.key === c.key;
                  const ariaSort = active
                    ? (sort.dir === 'asc' ? 'ascending' : 'descending')
                    : (sortable ? 'none' : undefined);
                  return (
                    <th
                      key={c.key}
                      scope="col"
                      // `'none'` rather than omitted when sortable-but-inactive, matching
                      // the house `SortableHeader`: it tells a screen reader the column
                      // CAN be sorted, which an absent attribute does not.
                      aria-sort={ariaSort}
                      className={`px-3 py-2.5 uppercase tracking-wider font-semibold ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={() => onSort(c.key)}
                          title={`Sort by ${c.label}`}
                          className="inline-flex items-center gap-0.5 select-none font-semibold uppercase tracking-wider hover:text-gray-800 dark:hover:text-gray-200 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/40 rounded-sm"
                        >
                          {c.label}
                          {/* aria-hidden — `aria-sort` on the header already tells a
                              screen reader the same thing, in the standard way. */}
                          {active && <span aria-hidden="true">{sort.dir === 'asc' ? '↑' : '↓'}</span>}
                        </button>
                      ) : (
                        c.label
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleList.map((row, i) => {
                const key = keyFor(row, i);
                const highlighted = isRowHighlighted ? isRowHighlighted(row) : false;
                const isOpen = Boolean(expanded[key]);
                const expandable = hasExpansion && presentExtras(row).length > 0;

                const base = `border-b border-gray-100 dark:border-gray-800/60 transition-colors ${
                  highlighted ? 'bg-amber-50 dark:bg-amber-900/20' : ''
                }`;
                // The whole row is the control when there is something behind it — the
                // house drill-down idiom. A row with nothing to reveal stays inert rather
                // than becoming a button that does nothing.
                const rowProps = expandable
                  ? expandableRowProps({
                    expanded: isOpen,
                    onToggle: () => toggle(key),
                    label: `details for this ${label.replace(/s$/, '')}`,
                    className: `${base} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                      isOpen ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'rc-collapsed'
                    }`,
                  })
                  : { className: `${base} hover:bg-gray-50 dark:hover:bg-gray-800/50` };

                return (
                  <React.Fragment key={key}>
                    <tr {...rowProps}>
                      {columns.map((c, ci) => {
                        const isTitle = ci === 0;
                        const titleClasses = isTitle && expandable
                          ? `rc-title-cell ${isOpen ? 'rc-title-open' : ''}`
                          : '';
                        return (
                          <td
                            key={c.key}
                            data-label={c.label}
                            title={c.title ? c.title(row) : undefined}
                            className={`px-3 py-2.5 align-top text-gray-800 dark:text-gray-200 ${
                              c.align === 'right' ? 'text-right tabular-nums' : 'text-left'
                            } ${titleClasses}`}
                          >
                            {isTitle && expandable ? (
                              <span className="inline-flex items-start gap-1.5">
                                {/* Rotating chevron, same glyph and rotation as the GA4
                                    and Ads drill-downs. aria-hidden, because the row
                                    itself carries `aria-expanded`. */}
                                <svg
                                  className={`w-3 h-3 mt-1 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                                  viewBox="0 0 12 12"
                                  aria-hidden="true"
                                >
                                  <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                </svg>
                                <span className="min-w-0 break-words">{c.render(row)}</span>
                              </span>
                            ) : (
                              c.render(row)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {expandable && isOpen && (
                      <tr>
                        <td
                          colSpan={columnCount}
                          className="px-3 py-4 bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800/60"
                        >
                          {expansionPanel(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </ThinScroll>

        {serverPaged && pagination.total_pages > 1 && (
          <Pagination
            page={pagination.page}
            totalPages={pagination.total_pages}
            onPageChange={onPageChange}
            totalItems={pagination.total_items}
            perPage={pagination.limit}
            label={label}
          />
        )}

        {clientPaged && clientTotalPages > 1 && (
          <Pagination
            page={safeClientPage}
            totalPages={clientTotalPages}
            onPageChange={setClientPage}
            totalItems={list.length}
            perPage={CLIENT_PAGE_SIZE}
            label={label}
          />
        )}
      </div>
    </div>
  );
};

export default PaginatedTable;
